import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/** Tiny string-array list editor used in many places. */
export function StringListEditor({ items, onChange, placeholder, testidPrefix }) {
  const set = (i, v) => onChange(items.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {(items || []).map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            value={v}
            onChange={(e) => set(i, e.target.value)}
            placeholder={placeholder}
            data-testid={`${testidPrefix}-${i}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => remove(i)}
            data-testid={`${testidPrefix}-remove-${i}`}
          >
            <Trash2 className="h-4 w-4 text-stone-500" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="mr-1 h-3.5 w-3.5" /> Add
      </Button>
    </div>
  );
}

/** Homework question editor — used inside chapter editor. */
export function HomeworkEditor({ homework, onChange }) {
  const updateQ = (i, patch) =>
    onChange({
      ...homework,
      questions: homework.questions.map((q, idx) =>
        idx === i ? { ...q, ...patch } : q
      ),
    });
  const addQ = () =>
    onChange({
      ...homework,
      questions: [
        ...(homework.questions || []),
        { id: crypto.randomUUID(), type: "short_answer", question: "", expected_points: [] },
      ],
    });
  const removeQ = (i) =>
    onChange({
      ...homework,
      questions: homework.questions.filter((_, idx) => idx !== i),
    });

  return (
    <div className="space-y-4" data-testid="homework-editor">
      <div className="space-y-2">
        <Label>Instructions</Label>
        <Textarea
          rows={2}
          value={homework.instructions || ""}
          onChange={(e) => onChange({ ...homework, instructions: e.target.value })}
          data-testid="hw-instructions"
        />
      </div>
      {homework.questions.map((q, i) => (
        <div key={q.id || i} className="border border-stone-200 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-stone-500">Question {i + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeQ(i)}
              data-testid={`hw-q-remove-${i}`}
            >
              <Trash2 className="h-4 w-4 text-stone-500" />
            </Button>
          </div>
          <Textarea
            rows={2}
            value={q.question}
            onChange={(e) => updateQ(i, { question: e.target.value })}
            placeholder="Question text"
            data-testid={`hw-q-text-${i}`}
          />
          <div>
            <Label className="text-xs">Expected points</Label>
            <StringListEditor
              items={q.expected_points || []}
              onChange={(v) => updateQ(i, { expected_points: v })}
              placeholder="A point to look for"
              testidPrefix={`hw-q-${i}-pt`}
            />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addQ} data-testid="hw-add-q">
        <Plus className="mr-1 h-3.5 w-3.5" /> Add question
      </Button>
    </div>
  );
}

/** MCQ + Subjective test editor. */
export function TestEditor({ test, onChange }) {
  const updateMCQ = (i, patch) =>
    onChange({
      ...test,
      mcq: test.mcq.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    });
  const setMCQOption = (i, opt, val) =>
    onChange({
      ...test,
      mcq: test.mcq.map((m, idx) =>
        idx === i
          ? { ...m, options: m.options.map((o, oi) => (oi === opt ? val : o)) }
          : m
      ),
    });
  const addMCQ = () =>
    onChange({
      ...test,
      mcq: [
        ...test.mcq,
        {
          id: crypto.randomUUID(),
          question: "",
          options: ["", "", "", ""],
          correct_index: 0,
          explanation: "",
        },
      ],
    });
  const removeMCQ = (i) =>
    onChange({ ...test, mcq: test.mcq.filter((_, idx) => idx !== i) });

  const updateSubj = (i, patch) =>
    onChange({
      ...test,
      subjective: test.subjective.map((s, idx) =>
        idx === i ? { ...s, ...patch } : s
      ),
    });
  const addSubj = () =>
    onChange({
      ...test,
      subjective: [
        ...test.subjective,
        { id: crypto.randomUUID(), question: "", rubric: "" },
      ],
    });
  const removeSubj = (i) =>
    onChange({
      ...test,
      subjective: test.subjective.filter((_, idx) => idx !== i),
    });

  return (
    <div className="space-y-5" data-testid="test-editor">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Duration (minutes)</Label>
          <Input
            type="number"
            min={5}
            max={240}
            value={test.duration_minutes}
            onChange={(e) =>
              onChange({ ...test, duration_minutes: Number(e.target.value) || 30 })
            }
            data-testid="test-duration"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="font-medium text-sm">MCQs</div>
        {test.mcq.map((m, i) => (
          <div key={m.id || i} className="border border-stone-200 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-stone-500">MCQ {i + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeMCQ(i)}
                data-testid={`mcq-remove-${i}`}
              >
                <Trash2 className="h-4 w-4 text-stone-500" />
              </Button>
            </div>
            <Textarea
              rows={2}
              value={m.question}
              onChange={(e) => updateMCQ(i, { question: e.target.value })}
              placeholder="Question"
              data-testid={`mcq-q-${i}`}
            />
            <div className="space-y-1.5">
              {m.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={m.correct_index === oi}
                    onChange={() => updateMCQ(i, { correct_index: oi })}
                    aria-label={`Option ${oi + 1} correct`}
                    data-testid={`mcq-${i}-correct-${oi}`}
                  />
                  <Input
                    value={opt}
                    onChange={(e) => setMCQOption(i, oi, e.target.value)}
                    placeholder={`Option ${oi + 1}`}
                    data-testid={`mcq-${i}-opt-${oi}`}
                  />
                </div>
              ))}
            </div>
            <Textarea
              rows={1}
              value={m.explanation}
              onChange={(e) => updateMCQ(i, { explanation: e.target.value })}
              placeholder="Explanation (shown after submit)"
              data-testid={`mcq-explanation-${i}`}
            />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addMCQ} data-testid="add-mcq">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add MCQ
        </Button>
      </div>

      <div className="space-y-3">
        <div className="font-medium text-sm">Subjective</div>
        {test.subjective.map((s, i) => (
          <div key={s.id || i} className="border border-stone-200 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-stone-500">Subjective {i + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeSubj(i)}
                data-testid={`subj-remove-${i}`}
              >
                <Trash2 className="h-4 w-4 text-stone-500" />
              </Button>
            </div>
            <Textarea
              rows={2}
              value={s.question}
              onChange={(e) => updateSubj(i, { question: e.target.value })}
              placeholder="Question"
              data-testid={`subj-q-${i}`}
            />
            <Textarea
              rows={2}
              value={s.rubric}
              onChange={(e) => updateSubj(i, { rubric: e.target.value })}
              placeholder="Grading rubric"
              data-testid={`subj-rubric-${i}`}
            />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addSubj} data-testid="add-subj">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add subjective
        </Button>
      </div>
    </div>
  );
}
